import React from "react";
import { MapPin, Phone, CheckCircle } from "lucide-react";
import mapImage from "../assets/Service Map.png";
import { ServicedCounties, ServiceFeatures } from "../constants";
const ServiceArea = () => {
  return (
    <div className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pt-28 pb-20 sm:py-24 lg:py-32 overflow-hidden">
      {/* Animated glow effect */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-500/10 via-transparent to-orange-600/5" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-orange-500/20 rounded-full blur-[120px] glow-pulse" />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-slate-900/60" />

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-6">
            <MapPin className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-medium text-orange-300">
              Where We Serve
            </span>
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Our{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Service Area
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 sm:text-xl max-w-2xl mx-auto leading-relaxed">
            Proudly serving communities across North Carolina with professional
            gas line services.
          </p>
        </div>

        {/* Map Section */}
        <div className="mt-20">
          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 sm:p-8 lg:p-12">
            {/* Map Container */}
            <div className="relative">
              <img
                src={mapImage}
                alt="Service Area Map of North Carolina"
                className="w-full h-auto rounded-xl"
              />

              {/* Legend - Responsive positioning */}
              <div className="mt-4 sm:mt-0 sm:absolute sm:bottom-4 sm:right-4 bg-slate-900/90 backdrop-blur-sm border border-slate-700 rounded-lg p-4 shadow-xl max-w-full sm:max-w-xs">
                <h3 className="text-white font-semibold mb-3 text-sm">
                  Legend
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-600 rounded flex-shrink-0"></div>
                    <span className="text-slate-300 text-sm">Service Area</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Counties List */}
            <div className="mt-8 border-t border-slate-700 pt-8">
              <h3 className="text-white font-semibold text-xl mb-6 text-center">
                Counties We Serve
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {ServicedCounties.map((county, index) => (
                  <div
                    key={county}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    className="flex items-center gap-2 bg-slate-800/70 border border-slate-700/50 rounded-lg px-4 py-3 hover:border-orange-500/50 transition-all fade-in-up"
                  >
                    <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                    <span className="text-slate-300 text-sm font-medium">
                      {county}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Service Features */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {ServiceFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                style={{ animationDelay: `${index * 0.15}s` }}
                className="bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 fade-in-up"
              >
                <div className="rounded-lg p-3 bg-orange-500/10 inline-flex mb-4">
                  <Icon className="h-8 w-8 text-orange-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-300 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Not in Service Area CTA */}
        <div className="mt-16 bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 sm:p-10 text-center">
          <MapPin className="h-12 w-12 text-orange-400 mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Don't See Your Area?
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-6">
            We're always looking to expand our service area. Contact us to
            discuss your gas line needs—we may be able to help or refer you to a
            trusted professional.
          </p>
          <a
            href="tel:9805406064"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
          >
            <Phone className="h-5 w-5" />
            Call (980) 540-6064
          </a>
        </div>

        {/* Service Commitment */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-white mb-6">
            Committed to Our Community
          </h2>
          <p className="text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
            As a locally-owned business, we're invested in the communities we
            serve. We understand the unique needs of North Carolina homeowners
            and businesses, and we're proud to deliver reliable, professional
            gas line services throughout our service area.
          </p>
          {/* <a
            href="request-appointment"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-8 py-4 text-lg font-semibold text-white shadow-lg hover:shadow-orange-500/50 hover:scale-105 transition-all"
          >
            temp comment
            Request Service
          </a> */}
          <p className="mt-6 text-sm text-slate-400">
            Licensed & Insured • Serving North Carolina since 2009
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes glowPulse {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .glow-pulse {
          animation: glowPulse 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ServiceArea;
